import React from 'react';
import { useTheme } from 'styled-components';
import { formatNZD, useSegment } from '@sparknz/journeys-common';
import { Box, Button, Text } from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { useSelectedPlans } from 'components/shop/MobilePlans/providers';

export interface SelectedPlanRowProps {
  basePrice: number;
  basePriceExcludingTax: number | null;
  discountPrice: number | null;
  discountedPriceExcludingTax: number | null;
  discountPercentage: number | null;
  discountPercentageExcludingTax: number | null;
  effectivePrice: number;
  effectivePriceExcludingTax: number | null;
  offerId: string;
  planName: string;
  quantity: number;
  isRemovable?: boolean;
}

export default function SelectedPlanRow({
  basePrice,
  basePriceExcludingTax,
  discountPrice,
  discountedPriceExcludingTax,
  discountPercentage,
  discountPercentageExcludingTax,
  effectivePrice,
  effectivePriceExcludingTax,
  offerId,
  planName,
  quantity,
  isRemovable,
}: SelectedPlanRowProps) {
  const {
    palette: { baseColors },
  } = useTheme();

  const { decreaseSelectedPlan } = useSelectedPlans();
  const { isBusiness } = useSegment();
  const discountedPercentage = isBusiness
    ? discountPercentageExcludingTax
    : discountPercentage;

  function handleRemoveClick() {
    trackCustomLink('Remove');
    decreaseSelectedPlan(offerId, planName);
  }

  const applicableDiscountPrice = isBusiness
    ? discountedPriceExcludingTax
    : discountPrice;

  const hasAstreikSymbol = isBusiness ? <sup>*</sup> : null;
  const effectivePriceValue = isBusiness
    ? effectivePriceExcludingTax
    : effectivePrice;
  const basePriceValue = isBusiness ? basePriceExcludingTax : basePrice;
  const discountPercentageValue = discountedPercentage
    ? ` (${discountedPercentage}%)`
    : null;

  if (!(effectivePriceValue && basePriceValue)) {
    throw new Error(`Missing tax exclusive prices for offer: ${offerId}`);
  }

  return (
    <div>
      <Box flexDirection="row" justifyContent="space-between">
        <Text variant="bodySMedium">{planName}</Text>
        <Text variant="bodySMedium">
          {formatNZD(effectivePriceValue)}
          {hasAstreikSymbol}
        </Text>
      </Box>

      {applicableDiscountPrice ? (
        <Box flexDirection="row" justifyContent="space-between" mb="s1">
          <Text variant="bodySMedium" color={baseColors.mawhero}>
            {formatNZD(applicableDiscountPrice)}
            {hasAstreikSymbol} savings
            {discountPercentageValue}
          </Text>
          <Text variant="bodySMedium" color={baseColors.mawheranui}>
            <s>
              {formatNZD(basePriceValue)}
              {hasAstreikSymbol}
            </s>
          </Text>
        </Box>
      ) : null}

      <Box flexDirection="row" justifyContent="space-between" mb="s3">
        <Text
          variant="bodySMedium"
          color={baseColors.mawheranui}
        >{`Quantity: ${quantity}`}</Text>

        {isRemovable ? (
          <Button variant="textbutton" onClick={handleRemoveClick}>
            Remove
          </Button>
        ) : null}
      </Box>
    </div>
  );
}
